// 訂貨's accounting for the 選品 list — reading it, not computing it.
//
// WHY THIS FILE EXISTS. Magnus asked the same question three times in a row:
//   「訂貨 tab also doesn't show all the items in the 清單」
//   「why 訂貨 didn't show all the items in my 選品 but 門市 no stock」
//   「still why not showing all the 選品 item list items?」
// Each answer was a reconciliation run in a terminal and pasted at him. The data was correct every
// time — 73 approved in the database, 73 served by the API, 73 rendered on 選品 — and it never
// helped, because 訂貨 draws only the styles with pieces to order. He counted ~37, could not
// account for the other 36 anywhere on screen, and so he asked a person. Three times. THAT is the
// bug: not a missing style, a missing accounting.
//
// EVERYTHING HERE IS A READ. The partition is built once, server-side
// (noke-restock-v2-reconcile.accountApproved), where the six action lists are actually assembled
// and where disjointness can be guaranteed by construction. Re-deriving it from `order.groups` and
// `today.*` in the browser would produce a second opinion about the same 73 styles, and two
// opinions is how the original 「73 vs 37」 confusion got here. So: no bucketing logic below, no
// counting, no inference — a reader, a joiner, and one assertion.
import { approvedRows } from './counterList';
import {
  RestockV2Accounting, RestockV2AccountingBucket, RestockV2ListApproved, RestockV2Order,
  RestockV2Plan,
} from './types';

/**
 * The server's accounting, or null — null whenever the screen must show nothing at all.
 *
 * It hangs off the PLAN, not off `order`: it accounts for approved styles across every list, so a
 * 留著 that moves a style out of 帶回 moves it between buckets while changing nothing about the
 * Korea order. Two backend tests assert that invariant by deep-equalling `plan.order`.
 */
export function accountingOf(plan: RestockV2Plan | null | undefined): RestockV2Accounting | null {
  const raw = plan?.accounting;
  // `ready: false` is the honest pre-curation state (no counter list → the engine picks
  // everything → there is no 「his N 款」 to account for). A backend that predates this field is
  // the same case. Both render nothing, rather than an accounting of zero.
  if (!raw || raw.ready !== true) return null;
  if (!Array.isArray(raw.buckets)) return null;
  return raw;
}

/** Buckets with something in them — what gets a tappable chip and a fold. */
export function filledBuckets(accounting: RestockV2Accounting | null): RestockV2AccountingBucket[] {
  return (accounting?.buckets || []).filter((bucket) => bucket.count > 0);
}

/**
 * Buckets with nothing in them.
 *
 * Drawn too, quietly and unclickable. A 0 is part of the arithmetic: 「0 沒有下單連結」 is the
 * line that tells him nothing is stuck, and dropping the empties would leave a row of chips that
 * looks complete without being complete.
 */
export function emptyBuckets(accounting: RestockV2Accounting | null): RestockV2AccountingBucket[] {
  return (accounting?.buckets || []).filter((bucket) => bucket.count <= 0);
}

/**
 * Does the partition still add up?
 *
 * The server asserts this itself (`ok`) and the arithmetic cannot fail by construction — so this
 * is not defensive politeness, it is the one thing that must never be silently wrong. A count he
 * has been told to trust, that does not add up, sends him straight back to asking a person, only
 * now with false confidence. When it disagrees the screen says so instead of showing the numbers.
 */
export function accountingAddsUp(accounting: RestockV2Accounting | null): boolean {
  if (!accounting) return true;
  const summed = (accounting.buckets || []).reduce((sum, bucket) => sum + (Number(bucket.count) || 0), 0);
  return accounting.ok !== false && summed === Number(accounting.total);
}

export const ACCOUNTING_BROKEN = '這裡的分類加起來跟清單款數對不上，先別信這些數字，請回報。';

/** 「清單 73 款：37 要訂、17 已經夠了…」 — the server's sentence, never rebuilt here. */
export function accountingLine(accounting: RestockV2Accounting | null): string {
  return accounting?.line || '';
}

/** The same sentence for 選品, said from where he is standing when he reads 73. */
export function accountingListLine(plan: RestockV2Plan | null | undefined): string {
  const line = accountingLine(accountingOf(plan));
  return line ? `訂貨：${line.replace(/^清單 \d+ 款：/, '')}` : '';
}

// ---- opening a bucket -------------------------------------------------------------------------
// A count he cannot open is the same dead end in a different shape: 「17 已經夠了」 with no way to
// see WHICH 17 is one more number to take on faith. The rows come from `plan.list.approved` — the
// same array 選品 renders — so the styles he opens here are recognisably the styles he approved
// there, with the same titles.

export interface AccountingStyle {
  sku: string;
  title: string | null;
  season: RestockV2ListApproved['season'] | null;
  cat: string | null;
  /** Whether the counter has it hanging right now; null when the list row does not say. */
  onRail: boolean | null;
  /** The engine's own sentence about this style, when it has one. Never invented here. */
  laneReason: string | null;
}

/**
 * The styles behind one bucket, in the bucket's own order.
 *
 * A SKU with no `list.approved` row still yields a row — bare, carrying only the SKU. Dropping it
 * would make the fold shorter than its own count, which is exactly the arithmetic-that-does-not-
 * add-up this feature exists to eliminate. Being unable to name a style is a thing to show him,
 * not a thing to hide.
 */
export function bucketStyles(
  plan: RestockV2Plan | null | undefined, bucket: RestockV2AccountingBucket | null | undefined,
): AccountingStyle[] {
  const index: Record<string, RestockV2ListApproved> = {};
  approvedRows(plan?.list ?? null).forEach((row) => { if (row?.sku) index[row.sku] = row; });
  return (bucket?.skus || []).map((sku) => {
    const row = index[sku];
    return {
      sku,
      title: row?.title ?? null,
      season: row?.season ?? null,
      cat: row?.cat ?? null,
      onRail: typeof row?.on_rail === 'boolean' ? row.on_rail : null,
      laneReason: row?.lane_reason || null,
    };
  });
}

// ---- the safety net --------------------------------------------------------------------------
// `order.unaccounted` is the coverage ledger's output and is empty on every healthy plan. When it
// is not empty a style stopped reaching every list, and the sentence the SERVER wrote about it has
// never been rendered anywhere until now — only the row cards were. The count is the thing he can
// read at a glance, so it is the thing that must be impossible to miss.
export function unaccountedLine(order: RestockV2Order | undefined): string {
  const line = (order?.unaccounted_line || '').trim();
  if (line) return line;
  // A backend that sends rows without the sentence still gets a loud line rather than a silent
  // section: the rows are the alarm, the sentence is only how it is worded.
  const rows = (order?.unaccounted || []).length;
  return rows ? `${rows} 款沒有進到任何清單，請回報` : '';
}
